import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { toast } from "react-toastify";
import ReactCodeInput from "react-code-input";

import FormInput from "../common/FormInput";
import Button from "../common/Button";
import Loader from "../common/Loader";
import {
  postUserLoggedAsCredential,
  postUserImpersonate,
  getGoPeopleUserProfile,
  getGoPeopleOrganizationsCommunity,
} from "../../api";
import {
  validateEmail,
  cleanEmail,
  logout,
  transformGoApiUser,
  setCookie,
  updateAuthCookieGoPeopleOrganizationId,
} from "../../utils";
import {
  setFolderFetched,
  setAuthTokenUser,
  setAuthToken,
  setJwtToken,
  setRefreshToken,
  setExpiresIn,
  setTokenCreatedAt,
  setAuthScope,
  setNavCommunity,
  fetchFiduciary,
} from "../../store";
import _ from "../../i18n";
import styles from "./LoggedAs.module.scss";
import { APP_ENV } from "../../config";
import { extractAuthContextFromJWT, isJWTValid } from "../../lib/jwt";

const LoggedAs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const fiduciary = useSelector((state) => state.folder.fiduciary);

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [errors, setErrors] = useState({
    email: "",
    code: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  const handleBlurEmail = (e) => {
    if (!validateEmail(cleanEmail(email))) {
      setErrors({ ...errors, email: _("validate_email") });
    } else {
      setErrors({ ...errors, email: "" });
    }
  };

  const validate = () => {
    let success = true;
    if (!validateEmail(cleanEmail(email))) {
      setErrors({ ...errors, email: _("validate_email") });
      success = false;
    }
    if (code.length < 6) {
      setErrors({ ...errors, code: _("validate_code") });
      success = false;
    } else {
      setErrors({ ...errors, code: "" });
    }

    return success;
  };

  const handleAuthSuccess = async (data) => {
    try {
      if (!data.accessToken) {
        throw new Error("No JWT token");
      }

      if (!isJWTValid(data.accessToken)) {
        throw new Error("Invalid JWT");
      }

      const authC = extractAuthContextFromJWT(data.accessToken);
      const dtExpire = new Date();
      dtExpire.setTime(dtExpire.getTime() + authC.expiresAt * 1000);
      const authData = {
        jwtToken: data.accessToken,
        refreshToken: data.refreshToken,
        token: authC.ttpAccessToken,
        expiresIn: authC.expiresAt,
        createdAt: Math.floor(Date.now() / 1000),
        scope: authC.scope,
      };

      setCookie(
        `ttp_auth_${APP_ENV}`,
        JSON.stringify(authData),
        dtExpire,
        "/",
        "unitedassociates.be"
      );

      setCookie(`ttp_auth_${APP_ENV}`, JSON.stringify(authData), dtExpire, "/");

      const userResponse = await getGoPeopleUserProfile(data.accessToken);

      let organizationsResponse;
      try {
        organizationsResponse = await getGoPeopleOrganizationsCommunity();
      } catch (e) {
        organizationsResponse = null;
      }

      dispatch(setAuthToken(authC.ttpAccessToken));
      dispatch(setJwtToken(data.accessToken));
      dispatch(setRefreshToken(data.refreshToken));
      dispatch(setExpiresIn(authC.expiresAt));
      dispatch(setTokenCreatedAt(authC.createdAt));
      dispatch(setAuthScope(authC.scope));

      const dataRoot = userResponse.data.data[0];

      let selectedCommunity =
        dataRoot.communities.find(
          (c) => c.uuid === dataRoot.user.selected_community_id
        ) || dataRoot.communities[0];

      const authData1 = {
        jwtToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresIn: authC.expiresAt,
        createdAt: data.createdAt,
        scope: data.scope,
        userGoApiId: dataRoot.user.uuid,
        communityGoApiId: selectedCommunity?.uuid,
        communityApiId: selectedCommunity?.ttp_community_id,
      };

      const transformed = transformGoApiUser(
        dataRoot,
        selectedCommunity,
        organizationsResponse?.data?.data
      );

      dispatch(
        setAuthTokenUser({
          token: {
            access_token: authC.ttpAccessToken,
            expires_in: authC.ttpExpiresIn,
            scope: authC.ttpScope,
            token_type: "bearer",
          },
          data: transformed,
        })
      );

      dispatch(setNavCommunity(transformed?.navCommunity || null));

      dispatch(fetchFiduciary());
      updateAuthCookieGoPeopleOrganizationId(
        data.accessToken,
        selectedCommunity?.uuid,
        selectedCommunity?.ttp_community_id,
        false,
        dataRoot.user.ttp_user_id
      );
    } catch (error) {
      console.error("Auth error:", error);
      throw error;
    }
  };
  const handleLogin = () => {
    if (!validate()) {
      return null;
    }

    const data = {
      // email: auth.user.mainEmail,
      code,
      loggedAsEmail: email,
      email: email,
    };

    setIsSaving(true);
    postUserImpersonate(data, auth.jwtToken).then(async (resp) => {
      try {
        logout(false);

        await handleAuthSuccess(resp)

        dispatch(setFolderFetched(false));
        navigate("/home");
      } catch (e) {
        toast.error(_("error"));
      } finally {
        setIsSaving(false);
      }
    });
  };

  return (
    <div className="grid-container">
      <div className={styles.content}>
        <h1 className={styles.title}>{_("logged_as")} :</h1>

        <FormInput
          name="email"
          value={email}
          label={_("email")}
          error={errors.email}
          className="ttp-input-lg"
          labelClassName="ttp-label-lg"
          autocomplete="username"
          onChange={(e) => setEmail(e.target.value)}
          onBlur={(e) => handleBlurEmail(e)}
        />

        <label className="ttp-label ttp-label-lg">Votre code</label>
        <div className={styles.codeBox}>
          <ReactCodeInput
            type="number"
            fields={6}
            value={code}
            onChange={(value) => setCode(value)}
            autoFocus={false}
            className={styles.codeInput}
            inputStyle={{
              marginRight: "0.463rem",
              width: "34px",
              borderRadius: "4px",
              fontSize: "14px",
              height: "44px",
              backgroundColor: "#F8F9FA",
              border: "1px solid #B6BFC8",
              textAlign: "center",
            }}
          />
        </div>
        {errors.code && <span className="ttp-error">{errors.code}</span>}

        <div className={styles.actions}>
          {isSaving ? (
            <Button
              variant="primary"
              style={{ paddingTop: "15px", paddingBottom: "15px" }}
            >
              <Loader
                style={{
                  height: "10px",
                }}
                color={"#fff"}
              />
            </Button>
          ) : (
            <Button onClick={handleLogin}>{_("signin")}</Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoggedAs;
